import React, { useDebugValue } from "react";
import Img from "gatsby-image";
import { Link } from "@reach/router";
import { StaticQuery, graphql } from "gatsby";

import { ProductTypes } from "../../types";
import Container from "../../components/Container/container";
import { tearribleStyles } from "../../types";
import "./product-select.scss";
import { useDataLayer } from "../../hooks/useDataLayer";
import { useGTagProducts } from "../../hooks/useGTagProducts";
import { currencySymbols } from "../../data/currency";

/**
 * @function noSort
 * Does not do any sorting.
 */
const noSort = () => {
	return 0;
};

const vipExclude = ["maximus"];

const limitedEditionImage = (
	<StaticQuery
		query={graphql`
			query LimitedEditionImage {
				file(relativePath: { eq: "limited-edition.png" }) {
					childImageSharp {
						fluid(maxWidth: 1080) {
							...GatsbyImageSharpFluid_withWebp_noBase64
						}
					}
				}
			}
		`}
	>
		{(data) => (
			<Img
				fluid={data.file.childImageSharp.fluid}
				alt="Limited edition Tearribles"
			/>
		)}
	</StaticQuery>
);

const ProductSelect = ({
	data,
	title,
	collections = null,
	sort = noSort,
	preOrderAuthorized,
	displayPrice = true,
	description,
}) => {
	const collectionNames = data?.allShopifyCollection?.edges?.map(
		({ node }) => {
			return node.title;
		},
	);

	const handle = data?.allShopifyCollection?.edges?.[0]?.node?.handle;

	const products = data?.allShopifyCollection?.edges
		.filter(({ node }) => {
			// If no collection is specified, return all.
			if (!collections) {
				return true;
			}

			return collectionNames.includes(node.title);
		})
		.reduce((acc, next) => {
			// Merge all products from each collection into
			// one Array.
			if (!next.node || !next.node.products) {
				return acc;
			}
			return [...acc, ...next.node.products];
		}, [])
		// Filter for VIP products
		.filter((node) => {
			if (preOrderAuthorized) {
				return true;
			}

			const { handle, tags } = node;
			return !tags.includes("VIP");
		})
		// .filter((node) => {
		//     const { variants } = node;
		//     let result = false;
		//     variants.forEach((variant) => {
		//         if (variant.availableForSale) {
		//             result = true;
		//         }
		//     })
		//     return result;
		// })
		.map((node) => {
			const { handle } = node;
			const size = handle
				.split("-")
				.filter((item) => item !== "tearrible")
				.filter((item) => item !== "sniff")
				.filter((item) => item !== "play")
				.filter((item) => item !== "combo")
				.filter((item) => item !== "chew")
				.filter((item) => item !== "1")
				.filter((item) => item !== "2")
				.filter((item) => item !== "3")
				.filter((item) => item !== "buy")
				.filter((item) => item !== "get")
				.filter((item) => item !== "free")
				.filter((item) => item !== "play")[0]
				.toLowerCase();

			node.size = size;
			return node;
		})
		.filter((node) => {
			if (preOrderAuthorized && vipExclude.includes(node.size)) {
				return false;
			}
			return true;
		})
		.filter((node) => !node.tags.includes("Hide"))
		.sort(sort)
		.reverse() // Reverse to set the correct order once the sold out sort is applied.
		.sort(({ variants, tags }) => {
			// Sort Sold Out items to the end.
			const available = variants.find(({ availableForSale }) => {
				return availableForSale && !tags.includes("Sold Out");
			});

			if (available) {
				return -1;
			} else {
				return 1;
			}
		});

	useGTagProducts(products, collectionNames);

	const renderProduct = (node) => {
		const images = node?.featuredImage?.localFile?.childImageSharp?.fluid
			? node?.featuredImage?.localFile?.childImageSharp?.fluid
			: node?.images?.map((node) => node.localFile.childImageSharp.fluid);
		const { id, title, productType, handle, variants, tags } = node;
		const size = handle.split("-")[1].toLowerCase();
		const name = title
			.replace(" - Replacement Parts", " Parts")
			.replace(" - Pre-Order", "")
			.split(" - ")
			.pop()
			.toLowerCase();
		const limited = tags.includes("Limited Edition");

		const details = tearribleStyles[size];
		const compareAtPrice = variants.reduce((acc, variant) => {
			// Multiple compare at prices.
			// if (acc && variant.compareAtPrice !== acc) {
			//     return null;
			// }

			// Return lowest compareAtPrice.
			if (
				variant.compareAtPrice !== variant.price &&
				acc > variant.compareAtPrice
			) {
				return variant.compareAtPrice;
			}

			if (!acc && variant?.compareAtPrice) {
				return variant.compareAtPrice;
			}

			return acc;
		}, false);

		const currencyCode =
			variants[0]?.presentmentPrices?.[0]?.price?.currencyCode;
		const currency = currencySymbols.get(currencyCode);

		const hasAvailableItem = variants.find(
			(variant) => variant.availableForSale,
		);
		const variantPrices = variants.reduce((acc, variant) => {
			if (!variant.availableForSale && hasAvailableItem) {
				return acc;
			}

			if (!acc.lowPrice) {
				return {
					lowPrice: variant.price,
					price: variant.price,
				};
			}

			const formatPrice = (price) => {
				return parseInt(
					typeof price === "string" && price?.replace(".", ""),
				);
			};

			if (formatPrice(acc.lowPrice) > formatPrice(variant.price)) {
				acc.lowPrice = variant.price;
			}

			if (formatPrice(acc.price) < formatPrice(variant.price)) {
				acc.price = variant.price;
			}

			return acc;
		}, {});

		const soldOut = !hasAvailableItem || tags.includes("Sold Out");
		const { lowPrice, price } = variantPrices;
		const onSale = compareAtPrice && lowPrice !== compareAtPrice;
		const preOrder = tags.includes("Pre Order");

		return (
			<div className="product-select__member" key={id}>
				<Link to={`/product/${handle}`}>
					<div
						className={`product-select__image-wrapper ${
							tags.includes("Full Image")
								? "product-select__image-wrapper--full"
								: ""
						}`}
					>
						<div className="product-select__badges">
							{/* Display soldOut indicator */}
							{soldOut && !preOrder && (
								<div className="product-select__sold-out">
									SOLD OUT
								</div>
							)}

							{/* Display pre order indicator */}
							{preOrder && (
								<div className="product-select__sale">
									PRE-ORDER
								</div>
							)}

							{/* Display limited edition indicator */}
							{limited && !soldOut && (
								<div className="product-select__sale">
									LIMITED EDITION
								</div>
							)}

							{/* Display onSale indicator */}
							{/* {onSale && !soldOut && !preOrder &&
                                <div className="product-select__sale">SALE</div>} */}
						</div>

						<Img
							fluid={Array.isArray(images) ? images[0] : images}
							className="product-select__image"
							alt={title}
							style={details && details.imageStyles}
						/>
					</div>
					<div className="product-select__text">
						<div className="product-select__title">
							{name.includes("tearrible combo")
								? name.replace("tearrible combo", "combo")
								: name}
						</div>
						{displayPrice && (
							<div className="product-select__right">
								{/* TODO: Compare at price needs to be rethought because the harness has different
                            price points for different sizes. */}
								{/* {!soldOut && price !== compareAtPrice && <div className="product-select__compare-price">$ {compareAtPrice}</div>} */}
								{/* {compareAtPrice && price !== compareAtPrice && hasAvailableItem
                                && <span className="product-select__compare-price">${compareAtPrice}</span>
                            } */}
								{lowPrice !== price &&
									`${currency}${lowPrice} - `}
								{currencyCode === "USD" && "$"}
								{price || compareAtPrice}{" "}
								{currencyCode !== "USD" && currencyCode}
							</div>
						)}
					</div>

					{productType === ProductTypes.TEARRIBLE && (
						<div className="product-select__text">
							<div className="product-select__left">
								{details && details.height && (
									<div className="product-select__text-info">
										{details && details.height}{" "}
										{details?.heightDescription
											? details.heightDescription
											: "Tall"}
										<br />
									</div>
								)}
								<div className="product-select__text-info">
									{details && details.subHeader}
								</div>
							</div>
						</div>
					)}

					{productType === ProductTypes.POOP_BAG_HOLDER && (
						<div className="product-select__text">
							<div className="product-select__left">
								<div className="product-select__text-info">
									2 Color Options
									<br />
								</div>
							</div>
						</div>
					)}

					{productType === ProductTypes.POOP_BAG && (
						<div className="product-select__text">
							<div className="product-select__left">
								<div className="product-select__text-info">
									Jasmine Scented
									<br />
								</div>
							</div>
						</div>
					)}

					{productType === ProductTypes.COLLAR && (
						<div className="product-select__text">
							<div className="product-select__left">
								<div className="product-select__text-info">
									2 Sizes / 2 Colors
									<br />
								</div>
							</div>
						</div>
					)}
				</Link>
			</div>
		);
	};

	if (description && !Array.isArray(description)) {
		description = [description];
	}

	return (
		<>
			{handle === "limited-edition" && (
				<div className="product-select__limited-edition">
					{limitedEditionImage}
				</div>
			)}
			<div className="product-select">
				<Container>
					<h3 className="product-select__header">{title}</h3>
					{Array.isArray(description) &&
						description.length > 0 &&
						description.map((item, index) => (
							<p
								className="product-select__description"
								key={`prodcut-select__description__${index}`}
							>
								{item}
							</p>
						))}
					<div className="product-select__wrapper">
						{products.map(renderProduct)}
					</div>
				</Container>
			</div>
		</>
	);
};

export default ProductSelect;
